import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {useNavigate} from 'views/module';

import JobFeed from './job_feed/JobFeed';

import backend from 'utils/backend';

const JobView = (props) => {
  const {jobRn, statusProgressWrap: progressWrap, requester} = props;

  const navigate = useNavigate();

  const [refresh, setRefresh] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);

  const [jobData, setJob] = React.useState(null);
  const [client, setClient] = React.useState(null);
  const [applications, setApplications] = React.useState([]);
  const [jobMessages, setJobMessages] = React.useState({});
  const [latestAnnouncementMessage, setLatestAnnouncementMessage] = React.useState(null);
  const [jobMessagesLastEvaluatedKey, setJobMessagesLastEvaluatedKey] = React.useState(null);
  const [jobMessagesLoading, setJobMessagesLoading] = React.useState(false);

  const [feedTab, setFeedTab] = React.useState('INFORMATION');
  const requesterRn = requester.userRn;

  // Load the data
  React.useEffect(() => {
    progressWrap(async () => {
      const result = await backend.career.getJob(jobRn, true, true);
      const applicants = result.applicants;

      setJob(result.job);
      setClient(result.client);
      setApplications(applicants);

      if (applicants.length > 0) {
        setFeedTab('INTERNAL_APPLICATIONS');
      } else {
        setFeedTab('INFORMATION');
      }

      setIsLoading(false);
    });
  }, [jobRn, progressWrap, refresh]);
  React.useEffect(() => {
    setJobMessages({});
    setLatestAnnouncementMessage(null);
    setJobMessagesLastEvaluatedKey(null);
    jobBackend.load.loadMessages(jobRn, null);
  }, [jobRn, refresh]);

  React.useEffect(() => {
    if (jobData?.status === 'REVIEW') {
      navigate(`/career/${jobData.jobRn}/edit`);
    }
  }, [jobData, navigate]);

  const jobBackend = {
    load: {
      loadMessages: async (loadJobRn, lastEvaluatedKey) => {
        progressWrap(async () => {
          setJobMessagesLoading(true);
          const output = await backend.career.message.load(loadJobRn, lastEvaluatedKey, true, true);
          const outputMessages = output.messages;

          setJobMessages((messages) => {
            const newMessages = outputMessages.reduce((result, message) => {
              return {
                ...result,
                [message.messageRn]: message,
              };
            }, {});

            return {
              ...messages,
              ...newMessages,
            };
          });

          setLatestAnnouncementMessage((currentMessage) => {
            const latestLoadedAnnouncementMessage = outputMessages
                .filter((message) => message?.messageType === 'ANNOUNCEMENTS')
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;

            if (!latestLoadedAnnouncementMessage) {
              return currentMessage;
            }

            if (!currentMessage) {
              return latestLoadedAnnouncementMessage;
            }

            return new Date(latestLoadedAnnouncementMessage.createdAt).getTime() > new Date(currentMessage.createdAt).getTime() ?
              latestLoadedAnnouncementMessage :
              currentMessage;
          });

          setJobMessagesLastEvaluatedKey(output.lastEvaluatedKey);
          setJobMessagesLoading(false);
        }, {debugString: 'JOB_MESSAGE_LOAD'});
      },
    },
    job: {
      editStatus: async (status, onSuccess) => {
        progressWrap(async () => {
          const body = {
            status,
          };
          const result = await backend.career.updateJobStatus(jobData.jobRn, body);

          setJob((prev) => ({...prev, status: result.job.status}));

          onSuccess();

          return result;
        });
      },
    },
    applications: {
      denyApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'REJECTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
      acceptApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'ACCEPTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
    },
    messages: {
      create: async (messageType, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
              messageType,
              requester: {
                rn: requesterRn,
              },
            };
            const result = await backend.career.message.create(jobRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (result.message.messageType === 'ANNOUNCEMENTS') {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_CREATE'});
      },
      update: async (messageRn, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
            };
            const result = await backend.career.message.update(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_UPDATE'});
      },
      delete: async (messageRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            await backend.career.message.delete(jobRn, messageRn);

            setJobMessages((messages) => {
              const nextMessages = {...messages};
              delete nextMessages[messageRn];
              return nextMessages;
            });
            if (latestAnnouncementMessage?.messageRn === messageRn) {
              setLatestAnnouncementMessage(null);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_DELETE'});
      },
      addEmoji: async (messageRn, emoji, emojiRequesterRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji,
              requester: {
                rn: emojiRequesterRn,
              },
            };
            const result = await backend.career.message.emoji.add(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_EMOJI_ADD'});
      },
      removeEmoji: async (messageRn, emoji, emojiRequesterRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji,
              requester: {
                rn: emojiRequesterRn,
              },
            };
            const result = await backend.career.message.emoji.remove(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_EMOJI_REMOVE'});
      },
    },
  };

  if (isLoading) return null;

  return (
    <Box
      sx={{
        textAlign: 'left',
        width: {
          xs: '100%',
          md: '75%',
        },
      }}
    >
      <JobFeed
        jobRn={jobRn}
        jobData={jobData}
        applications={applications}
        client={client}
        jobBackend={jobBackend}
        jobMessages={jobMessages}
        latestAnnouncementMessage={latestAnnouncementMessage}
        jobMessagesLastEvaluatedKey={jobMessagesLastEvaluatedKey}
        jobMessagesLoading={jobMessagesLoading}
        feedTab={feedTab}
        setFeedTab={setFeedTab}
        refresh={refresh}
        setRefresh={setRefresh}
      />
    </Box>
  );
};

JobView.propTypes = {
  jobRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(JobView));
